import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useMemo, useCallback } from 'react';
import MuiSnackbar from '@mui/material/Snackbar';
import { styled } from '@mui/material';
import MuiBox from '@mui/material/Box';
import { ReactComponent as ForwardRef } from '../../assets/icons/CloseSmall.svg.js';
import ToastInfo from './components/ToastInfo.js';
import ToastActions from './components/ToastActions.js';
import ToastInlineAction from './components/ToastInlineAction.js';

const HIDE_DURATIONS = {
    active: null,
    passive: 5000,
};
const _MuiSnackbar = styled(MuiSnackbar, {
    shouldForwardProp: prop => !['hasIcon'].includes(prop),
}) `
  min-height: ${({ hasIcon }) => (hasIcon ? '72px' : '64px')};
`;
const ToastContent = styled(MuiBox) `
  display: flex;
  flex: 1;
  position: relative;
  margin: ${({ theme }) => theme.spacing(4, 3, 4, 4)};
  align-items: center;
  svg {
    margin: ${({ theme }) => theme.spacing(1.5, 0, 1.5, 0.5)};
  }
`;
const Root = styled(MuiBox) `
  display: flex;
  flex: 1;
  flex-direction: column;
  max-height: 132px;
`;
const CloseButton = styled(ForwardRef) `
  margin: ${({ theme }) => theme.spacing(-1, 0, 0, 0)} !important;
  color: ${({ theme }) => theme.palette.N30.main};
  cursor: pointer;
  position: absolute;
  right: 0px;
  top: 0px;
  :hover {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;
const Toast = (_a) => {
    var { type = 'passive', icon, title, text, hasCloseButton, actionButtons, inlineAction, onClose, 'data-testid': testId } = _a, rest = __rest(_a, ["type", "icon", "title", "text", "hasCloseButton", "actionButtons", "inlineAction", "onClose", 'data-testid']);
    const hasActions = !!(actionButtons === null || actionButtons === void 0 ? void 0 : actionButtons.length) || !!inlineAction;
    const hideDuration = useMemo(() => {
        let usedType = type;
        if (actionButtons || inlineAction) {
            usedType = 'active';
        }
        return HIDE_DURATIONS[usedType];
    }, [actionButtons, inlineAction, type]);
    const hasIcon = !!icon;
    const showCloseButton = useMemo(() => {
        if (actionButtons === null || actionButtons === void 0 ? void 0 : actionButtons.length) {
            return false;
        }
        return hasCloseButton;
    }, [actionButtons, hasCloseButton]);
    const onCloseButtonClick = useCallback((e) => {
        onClose === null || onClose === void 0 ? void 0 : onClose(e, 'clickaway');
    }, [onClose]);
    const onToastClose = useCallback((e, reason) => {
        if ((reason === 'clickaway' || reason === 'escapeKeyDown') && (type === 'active' || hasActions)) {
            return;
        }
        onClose === null || onClose === void 0 ? void 0 : onClose(e, reason);
    }, [hasActions, onClose, type]);
    return (jsx(_MuiSnackbar, Object.assign({}, rest, { autoHideDuration: hideDuration, hasIcon: hasIcon, onClose: onToastClose }, { children: jsxs(Root, Object.assign({ "data-testid": testId }, { children: [jsxs(ToastContent, { children: [icon, jsx(ToastInfo, Object.assign({ text: text, title: title, hasIcon: hasIcon, hasActions: hasActions, hasCloseButton: hasCloseButton }, { children: inlineAction && jsx(ToastInlineAction, Object.assign({}, inlineAction)) })), showCloseButton && jsx(CloseButton, { "data-testid": "close-icon", onClick: onCloseButtonClick })] }), !inlineAction && jsx(ToastActions, { actionButtons: actionButtons })] })) })));
};

export { Toast as default };
//# sourceMappingURL=Toast.js.map
